import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import TaxCalculator from '../components/TaxCalculator';
import FAQ from '../components/FAQ';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { updateMetaTags, defaultMeta } from '../utils/metaTags';

const { FiInfo, FiCheckCircle } = FiIcons;

const Home = () => {
  useEffect(() => {
    updateMetaTags({
      title: 'UK Take-Home Pay & Tax Calculator (2025/26) | Salary Toolkit',
      description: 'Calculate your UK take-home pay after tax, National Insurance, pension and student loan deductions. Free 2025/26 tax year calculator for England, Wales, Scotland & Northern Ireland.',
      keywords: 'UK salary calculator, take-home pay, tax calculator, National Insurance, student loan, pension, 2025/26 tax year, England, Wales, Scotland, Northern Ireland',
      ogTitle: 'UK Take-Home Pay & Tax Calculator (2025/26)',
      ogDescription: 'Calculate your UK take-home pay after tax, National Insurance, pension and student loan deductions. Free 2025/26 tax year calculator.',
      canonical: `${defaultMeta.siteUrl}/`,
    });
  }, []);

  // Extended salary range up to 200,000
  const popularSalaries = [
    20000, 25000, 30000, 35000, 40000, 45000, 50000, 60000, 70000, 80000, 90000, 100000,
    110000, 120000, 130000, 140000, 150000, 160000, 170000, 180000, 190000, 200000
  ];

  return (
    <div className="space-y-12">
      {/* Hero Section */}
      <section className="text-center space-y-6 py-6">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="inline-block px-4 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm font-bold mb-4"
        >
          Tax Year 2025 / 2026 Updated
        </motion.div>
        <motion.h1 
          className="text-4xl md:text-6xl font-black text-slate-900 leading-tight"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          UK Take-Home Pay <br className="hidden md:block" />
          <span className="text-blue-600">& Tax Calculator</span>
        </motion.h1>
        
        <motion.p 
          className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          Calculate your exact take-home pay after Income Tax, National Insurance, 
          Pension contributions, and Student Loans for salaries up to £200k+.
        </motion.p>
      </section>

      {/* Main Calculator */}
      <div className="relative">
        <div className="absolute inset-0 bg-blue-600/5 blur-3xl rounded-full -z-10 transform -translate-y-1/2" />
        <TaxCalculator />
      </div>

      {/* Comprehensive Guide Section */}
      <motion.section 
        className="bg-white rounded-2xl p-6 md:p-10 shadow-xl border border-slate-100 overflow-hidden relative"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <div className="absolute top-0 right-0 p-8 opacity-5 text-slate-900 pointer-events-none">
          <SafeIcon icon={FiInfo} size={120} />
        </div>
        
        <h2 className="text-3xl font-bold text-slate-900 mb-8 flex items-center gap-3">
          <div className="w-10 h-10 bg-indigo-100 text-indigo-600 rounded-xl flex items-center justify-center">
            <SafeIcon icon={FiInfo} />
          </div>
          Complete Guide to UK Tax & Take-Home Pay (2025/26)
        </h2>
        
        <div className="space-y-8">
          {/* Income Tax Section */}
          <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2 text-xl">
              <SafeIcon icon={FiCheckCircle} className="text-blue-500" />
              Income Tax Bands & Rates
            </h3>
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex justify-between items-center p-3 rounded bg-white shadow-sm">
                  <span className="font-medium">Personal Allowance</span>
                  <span className="text-blue-600 font-bold">£12,570 (0%)</span>
                </div>
                <div className="flex justify-between items-center p-3 rounded bg-white shadow-sm border-l-4 border-emerald-500">
                  <span className="font-medium">Basic Rate</span>
                  <span className="text-emerald-600 font-bold">20% (£12,571 - £50,270)</span>
                </div>
                <div className="flex justify-between items-center p-3 rounded bg-white shadow-sm border-l-4 border-amber-500">
                  <span className="font-medium">Higher Rate</span>
                  <span className="text-amber-600 font-bold">40% (£50,271 - £125,140)</span>
                </div>
                <div className="flex justify-between items-center p-3 rounded bg-white shadow-sm border-l-4 border-rose-500">
                  <span className="font-medium">Additional Rate</span>
                  <span className="text-rose-600 font-bold">45% (Over £125,140)</span>
                </div>
              </div>
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-slate-700 leading-relaxed">
                  <strong>Personal Allowance Taper:</strong> Your tax-free personal allowance of £12,570 is reduced by £1 for every £2 you earn above £100,000. This means if you earn £125,140 or more, you lose your entire personal allowance, effectively creating a 60% tax rate on earnings between £100,000 and £125,140. This is why many high earners choose to increase pension contributions to stay below this threshold.
                </p>
              </div>
            </div>
          </div>

          {/* National Insurance Section */}
          <div className="bg-indigo-50 rounded-xl p-6 border border-indigo-100">
            <h3 className="font-bold text-indigo-900 mb-4 flex items-center gap-2 text-xl">
              <SafeIcon icon={FiCheckCircle} className="text-indigo-500" />
              National Insurance Contributions (Class 1)
            </h3>
            <div className="space-y-4 text-indigo-900">
              <p className="text-base leading-relaxed">
                National Insurance (NI) is a tax on earnings that funds state benefits including the State Pension, Jobseeker's Allowance, and Employment and Support Allowance. For employees, NI is calculated on a weekly basis and deducted from your salary automatically.
              </p>
              <div className="bg-white rounded-lg p-4 border border-indigo-200">
                <h4 className="font-semibold mb-3">2025/26 NI Rates for Employees:</h4>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="font-bold text-indigo-600">0%</span>
                    <span>on weekly earnings below £242 (annual equivalent: below £12,570)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold text-indigo-600">8%</span>
                    <span>on weekly earnings between £242 and £967 (annual equivalent: £12,570 - £50,270)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold text-indigo-600">2%</span>
                    <span>on weekly earnings above £967 (annual equivalent: above £50,270)</span>
                  </li>
                </ul>
              </div>
              <div className="mt-4 p-3 bg-white/70 rounded-lg text-sm leading-relaxed">
                <strong>Important:</strong> NI rates were significantly reduced from 12% to 8% in January 2024. This reduction saves the average worker hundreds of pounds per year. Our calculator uses the latest confirmed 2025/26 figures from HMRC.
              </div>
            </div>
          </div>

          {/* Student Loans Section */}
          <div className="bg-emerald-50 rounded-xl p-6 border border-emerald-100">
            <h3 className="font-bold text-emerald-900 mb-4 text-xl">Student Loan Repayments</h3>
            <p className="text-emerald-900 mb-4 leading-relaxed">
              Student loan repayments are automatically deducted from your salary through PAYE (Pay As You Earn) if you earn above the threshold for your loan plan. The repayment is calculated as a percentage of your income above the threshold, not on your entire salary.
            </p>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div className="bg-white rounded-lg p-4 border border-emerald-200">
                <h4 className="font-semibold mb-2">Plan 1 (Pre-2012)</h4>
                <p className="text-emerald-800">Threshold: £22,015 | Rate: 9%</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-emerald-200">
                <h4 className="font-semibold mb-2">Plan 2 (2012-2023)</h4>
                <p className="text-emerald-800">Threshold: £27,295 | Rate: 9%</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-emerald-200">
                <h4 className="font-semibold mb-2">Plan 4 (Scotland)</h4>
                <p className="text-emerald-800">Threshold: £27,660 | Rate: 9%</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-emerald-200">
                <h4 className="font-semibold mb-2">Plan 5 (2023+)</h4>
                <p className="text-emerald-800">Threshold: £25,000 | Rate: 9%</p>
              </div>
            </div>
          </div>

          {/* Pension Section */}
          <div className="bg-amber-50 rounded-xl p-6 border border-amber-100">
            <h3 className="font-bold text-amber-900 mb-4 text-xl">Pension Contributions</h3>
            <p className="text-amber-900 mb-4 leading-relaxed">
              Under UK auto-enrolment rules, most employees are automatically enrolled in a workplace pension. The minimum total contribution is 8% of qualifying earnings, with at least 3% coming from your employer. You can choose to contribute more, and many financial advisors recommend contributing 10-15% of your salary (including employer contributions) for a comfortable retirement.
            </p>
            <div className="bg-white rounded-lg p-4 border border-amber-200 text-sm">
              <p className="text-amber-800 leading-relaxed">
                <strong>Tax Relief:</strong> Pension contributions receive tax relief at your highest rate of income tax. This means if you're a higher rate taxpayer (40%), contributing to your pension effectively costs you only 60% of the contribution amount. For example, a £100 pension contribution only costs you £60 in take-home pay.
              </p>
            </div>
          </div>

          {/* Regional Differences */}
          <div className="bg-purple-50 rounded-xl p-6 border border-purple-100">
            <h3 className="font-bold text-purple-900 mb-4 text-xl">Scotland vs Rest of UK Tax Differences</h3>
            <p className="text-purple-900 mb-4 leading-relaxed">
              Scotland has its own income tax rates and bands, which differ from England, Wales, and Northern Ireland. Scottish taxpayers pay different rates, which can result in higher or lower take-home pay depending on your salary level.
            </p>
            <div className="bg-white rounded-lg p-4 border border-purple-200 text-sm">
              <h4 className="font-semibold mb-2 text-purple-900">2025/26 Scottish Income Tax Rates:</h4>
              <ul className="space-y-1 text-purple-800">
                <li>• Starter rate: 19% (£12,571 - £14,876)</li>
                <li>• Basic rate: 20% (£14,877 - £26,561)</li>
                <li>• Intermediate rate: 21% (£26,562 - £43,662)</li>
                <li>• Higher rate: 42% (£43,663 - £75,000)</li>
                <li>• Advanced rate: 45% (£75,001 - £125,140)</li>
                <li>• Top rate: 48% (Over £125,140)</li>
              </ul>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Popular Salaries Grid */}
      <motion.section 
        className="space-y-6"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-slate-900">Popular Salary Calculations</h2>
          <Link to="/take-home-table" className="text-blue-600 font-semibold hover:underline flex items-center gap-1">
            View full table <SafeIcon icon={FiIcons.FiArrowRight} />
          </Link>
        </div>
        
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {popularSalaries.map(salary => (
            <Link
              key={salary}
              to={`/salary/${salary}`}
              className="group bg-white hover:bg-gradient-to-br hover:from-blue-600 hover:to-indigo-600 p-4 rounded-xl text-center border border-slate-200 shadow-sm transition-all hover:shadow-lg hover:-translate-y-1"
            >
              <div className="text-xs text-slate-500 group-hover:text-blue-100 font-medium mb-1">Take-home on</div>
              <div className="text-lg font-bold text-slate-900 group-hover:text-white">£{salary.toLocaleString()}</div>
            </Link>
          ))}
        </div>
      </motion.section>

      <FAQ />
    </div>
  );
};

export default Home;